import type { FreezeAppointmentRequest } from "@src/interfaces/freeze-appointment.interface";
import { callApi } from "@src/utils/api-client";
import { getFailureTypeForHttpStatus } from "@src/utils/failure-taxonomy";
import ClientLogsService from "./client-logs.service";
import { buildFreezeAppointmentConfig } from "./configs";
import { runManagedRequest } from "./request-manager";

/**
 * Freeze Appointment Service
 *
 * Service layer for freezing appointment slots to prevent double booking.
 * This service interfaces with the internal freeze-appointment API route.
 */

export type { FreezeAppointmentRequest } from "@src/interfaces/freeze-appointment.interface";

export interface FreezeAppointmentResponse {
  transactionId: string;
  message: string;
  frozenAt?: string; // ISO 8601 timestamp when appointment was frozen on server
}

type FreezeAppointmentApiResponse =
  | FreezeAppointmentResponse
  | {
      message?: string;
      error?: string;
      selectionRejected?: boolean;
    };

export interface FreezeAppointmentServiceResponse {
  success: boolean;
  data?: FreezeAppointmentResponse;
  error?: string;
  /** The freeze endpoint rejected this selection; transport errors leave this unset. */
  rejectedByServer?: boolean;
}

export async function freezeAppointmentSlots(
  request: FreezeAppointmentRequest,
): Promise<FreezeAppointmentServiceResponse> {
  const key = `freeze:${request.userUuid}:${request.practiceId}:${request.appointmentIds.join(",")}`;
  try {
    return await runManagedRequest({
      key,
      policy: "single-flight",
      request: async (signal) => {
        const { data, response } = await callApi(buildFreezeAppointmentConfig(request, signal));
        const result = (data ?? {}) as FreezeAppointmentApiResponse;
        const apiMessage =
          result.message || ("error" in result ? result.error : undefined) || "Unknown API error";
        let resultFromService: FreezeAppointmentServiceResponse;
        if (response.ok) {
          ClientLogsService.logWebAction(
            request.userUuid,
            "freeze-appointment.service.freezeAppointmentSlots",
            "Appointment slot successfully frozen",
            undefined,
            { value: result },
          );
          resultFromService = {
            success: true,
            data: result as FreezeAppointmentResponse,
          };
        } else {
          ClientLogsService.logError(
            request.userUuid,
            "freeze-appointment.service.freezeAppointmentSlots",
            `API error freezing appointment slot: ${apiMessage}`,
            new Error(apiMessage),
            {
              errorType: getFailureTypeForHttpStatus(response.status),
              dependencyName: "FreezeAppointmentAPI",
              functionName: "freezeAppointmentSlots",
            },
          );
          resultFromService = {
            success: false,
            error: apiMessage,
            ...("selectionRejected" in result && result.selectionRejected === true
              ? { rejectedByServer: true }
              : {}),
          };
        }

        return resultFromService;
      },
    });
  } catch (error) {
    ClientLogsService.logError(
      request.userUuid,
      "freeze-appointment.service.freezeAppointmentSlots",
      "Error freezing appointment slot",
      error instanceof Error ? error : new Error("Unknown error"),
      {
        errorType: "DependencyFailure",
        dependencyName: "FreezeAppointmentAPI",
        functionName: "freezeAppointmentSlots",
      },
    );
    return {
      success: false,
      error: error instanceof Error ? error.message : "An unexpected error occurred",
    };
  }
}
